import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalStore } from '@adsk/alloy-react-context';

import {
  readCachedDataByUrl,
  writeCachedDataByUrl,
} from '../helpers/cacheHelpers';
import { BaseDataStoreError } from '../helpers/createDataModificationStore';
import { fetchWithAccountType } from '../helpers/fetchWithAccountType';
import {
  EntitlementsProject,
  EntitlementsUser,
} from '../projectEntitlementTypes';

type ProjectEntitlementsResponse = {
  user: EntitlementsUser;
  currentProject: EntitlementsProject;
};

export type ProjectEntitlementsStore = {
  error: BaseDataStoreError | null;
  loading: boolean;
  _started: boolean;
  url: string | null;
  user?: EntitlementsUser;
  currentProject?: EntitlementsProject;
  setUser: (user?: EntitlementsUser) => void;
  setCurrentProject: (currentProject?: EntitlementsProject) => void;

  // Fetch methods
  fetchData: (url?: string | null) => Promise<void>;
  retry: () => Promise<void>;
};

const defaultFetchProjectEntitlements = (url: string) =>
  fetchWithAccountType<ProjectEntitlementsResponse>(url);

export const useProjectEntitlementsStore = (
  contextName: string = CONTEXT_NAMES.ProjectEntitlementsContext,
  fetchFunction: (
    url: string,
  ) => Promise<ProjectEntitlementsResponse> = defaultFetchProjectEntitlements,
) =>
  getOrCreateGlobalStore<ProjectEntitlementsStore>((set, get) => {
    return {
      error: null,
      loading: false,
      url: null,
      user: undefined,
      currentProject: undefined,
      _started: false,
      setUser: (newUser?: EntitlementsUser) => {
        set({ user: newUser });

        // Update cache with new user data
        const { url } = get();
        if (url) {
          const { currentProject } = get();
          writeCachedDataByUrl(contextName, url, {
            user: newUser,
            currentProject,
          });
        }
      },
      setCurrentProject: (currentProject?: EntitlementsProject) => {
        set({ currentProject });

        // Update cache with new project data
        const { url } = get();
        if (url) {
          const { user } = get();
          writeCachedDataByUrl(contextName, url, {
            user,
            currentProject,
          });
        }
      },

      fetchData: async (url) => {
        if (!url) {
          return;
        }

        // Set _started immediately to prevent race conditions
        // Check must happen before async operation begins
        const currentState = get();
        if (currentState._started && currentState.url === url) {
          return;
        }

        // Set loading state and guard flag atomically
        set({
          _started: true,
          loading: true,
          error: null,
          url,
        });

        // Load cached data immediately before fetching (only if not already loading)
        // This provides instant UI feedback while fresh data loads
        const cachedData = readCachedDataByUrl<ProjectEntitlementsResponse>(
          contextName,
          url,
        );
        if (cachedData) {
          set({
            user: cachedData.user,
            currentProject: cachedData.currentProject,
          });
        }

        try {
          const response = await fetchFunction(url);

          // Write to cache after successful fetch
          writeCachedDataByUrl(contextName, url, response);

          set({
            error: null,
            loading: false,
            user: response.user,
            currentProject: response.currentProject,
          });
        } catch (error) {
          set({
            error: error as Error,
            loading: false,
          });
        }
      },

      retry: async () => {
        const { fetchData, url } = get();
        set({ _started: false });
        await fetchData(url);
      },
    };
  }, contextName)();
